import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { hasMultiplePages, Paginator, toActivePage, toNumberOfPages, VariableLengthPaginator } from '@k2/common/pagination/paginator';
import { complement, range } from 'ramda';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'paginator',
  templateUrl: 'paginator.component.html',
  styleUrls: ['paginator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PaginatorComponent implements OnChanges {
  @Input() paginator: Paginator;

  private _customPageSizeOptions?: number[] = null;
  get customPageSizeOptions(): number[] {
    return this._customPageSizeOptions;
  }

  @Input() set customPageSizeOptions(value: number[]) {
    this._customPageSizeOptions = value;
  }

  private _pageSize?: number;
  get pageSize(): number {
    return this._pageSize;
  }

  @Input() set pageSize(value: number) {
    if (this._pageSize != value) {
      this._pageSize = value;

      (this.paginator as VariableLengthPaginator).updatePageSize(this.pageSize);
    }
  }

  @Input() alwaysVisible: boolean = false;

  numberOfPagesAsRange: Observable<number[]>;
  isHidden: Observable<boolean>;
  activePage: Observable<number>;
  cannotBeDecreased: Observable<boolean>;
  cannotBeIncreased: Observable<boolean>;

  ngOnChanges(): void {
    const { state } = this.paginator;
    if (this.customPageSizeOptions) {
      state.subscribe(state => {
        if (this.customPageSizeOptions.includes(state.pageSize)) {
          this.pageSize = state.pageSize;
        }
      });
    }

    const numberOfPages = state.pipe(map(toNumberOfPages));

    this.numberOfPagesAsRange = numberOfPages.pipe(map(n => range(0, n)));
    this.isHidden = state.pipe(map(complement(hasMultiplePages)));
    this.activePage = state.pipe(map(toActivePage));

    this.cannotBeDecreased = this.activePage.pipe(map(page => page === 0));
    this.cannotBeIncreased = combineLatest(this.activePage, numberOfPages).pipe(
      map(([activePage, numberOfPages]) => activePage === numberOfPages - 1)
    );
  }

  setPage = (page: number) => {
    this.paginator.updateBy(state => ({
      offset: page * state.pageSize,
      limit: state.pageSize
    }));
  };

  setPageToLast = () => {
    this.paginator.updateBy(state => {
      const lastPage = toNumberOfPages(state) - 1;

      return {
        offset: lastPage * state.pageSize,
        limit: state.pageSize
      };
    });
  };

  decreasePage = () => {
    this.paginator.updateBy(state => {
      const offset = state.offset - state.pageSize;

      if (offset < 0) return state;
      return { offset, limit: state.pageSize };
    });
  };

  increasePage = () => {
    this.paginator.updateBy(state => {
      const offset = state.offset + state.pageSize;

      if (offset >= state.length) return state;
      return { offset, limit: state.pageSize };
    });
  };

  changePageSize(value: string) {
    this.pageSize = parseInt(value);
  }
}
